import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Card from "components/card";
import { useDealData, sortDealPeriods } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import { sortPortfolioPeriods } from "contexts/PortfolioContext";
import { ExpenseTable, RevenueTable, EbitdarTable } from "./FinanceTables";
import TrendChart from "./TrendChart";

const FinanceChartPage = () => {
    const { chartType } = useParams();
    const [title, setTitle] = useState("");
    const [chartComponent, setChartComponent] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [renderKey, setRenderKey] = useState(0); // Key to force complete remount

    const { viewMode } = useViewMode();
    const {
        currentFacility: dealFacility,
        currentDeal,
        currentDealName,
        isDealLoading,
        selectedPeriod: dealSelectedPeriod
    } = useDealData();

    const {
        currentPortfolioFacility,
        isPortfolioLoading,
        selectedPeriod: portfolioSelectedPeriod
    } = usePortfolioData();

    // Determine which facility and period to use based on view mode
    const currentFacility = viewMode === 'portfolio' ? currentPortfolioFacility : dealFacility;
    const selectedPeriod = viewMode === 'portfolio' ? portfolioSelectedPeriod : dealSelectedPeriod;
    const isLoading = viewMode === 'portfolio' ? isPortfolioLoading : isDealLoading;

    // Extract all available time periods from the current facility data
    // Sort periods in the correct order: Years (ascending), T12/T6/T3/T1 (descending), Projections (ascending)
    const availableTimePeriods = useMemo(() => {
        if (!currentFacility || !currentFacility.periods) {
            return [];
        }

        // Get all period keys from the periods object
        const periods = Object.keys(currentFacility.periods);
        
        // Sort periods based on view mode
        if (viewMode === 'portfolio') {
            return sortPortfolioPeriods(periods);
        } else {
            return sortDealPeriods(periods);
        }
    }, [currentFacility, viewMode]);

    // Effect to reset everything on deal/portfolio change
    useEffect(() => {
        if (isLoading) {
            // Clear chart component during loading to avoid stale data display
            setChartComponent(null);
            // Create new render key to force complete remount
            setRenderKey(prev => prev + 1);
        }
    }, [isLoading, currentDealName]);

    useEffect(() => {
        if (!chartType || !currentFacility || isLoading || !selectedPeriod) {
            // Skip re-rendering during loading states
            return;
        }

        // Extract period data using selectedPeriod instead of activePeriod
        const periodData = currentFacility?.periods?.[selectedPeriod]?.Categories || {};
        const expenseData = periodData?.Expenses || [];
        const revenueData = periodData?.Revenue || [];

        // Check if current facility is a group
        const isGroup = currentFacility?.details?.isGroup;
        const groupPrefix = isGroup ? "Group " : "";

        // Set up the appropriate component and title based on the chart type
        switch (chartType) {
            case "revenue-details":
                setTitle(`${groupPrefix}Revenue Details`);
                setChartComponent(
                    <RevenueTable
                        data={revenueData}
                        availableTimePeriods={availableTimePeriods}
                        currentPeriod={selectedPeriod}
                    />
                );
                break;
            case "expense-details":
                setTitle(`${groupPrefix}Expense Details`);
                setChartComponent(
                    <ExpenseTable
                        data={expenseData}
                        availableTimePeriods={availableTimePeriods}
                        currentPeriod={selectedPeriod}
                    />
                );
                break;
            case "ebitdar-details":
                setTitle(`${groupPrefix}EBITDAR Details`);
                setChartComponent(
                    <EbitdarTable
                        data={expenseData}
                        availableTimePeriods={availableTimePeriods}
                        currentPeriod={selectedPeriod}
                    />
                );
                break;
            case "expense-trends":
                setTitle(`${groupPrefix}Expense Trends`);
                setChartComponent(
                    <TrendChart
                        key={`expense-trends-${currentDealName}-${selectedPeriod}`}
                        data={currentFacility?.periods || {}}
                        type="Expenses"
                    />
                );
                break;
            case "revenue-trends":
                setTitle(`${groupPrefix}Revenue Trends`);
                setChartComponent(
                    <TrendChart
                        key={`revenue-trends-${currentDealName}-${selectedPeriod}`}
                        data={currentFacility?.periods || {}}
                        type="Revenue"
                    />
                );
                break;
            case "ebitdar-trends":
                setTitle(`${groupPrefix}EBITDAR Trends`);
                setChartComponent(
                    <TrendChart
                        key={`ebitdar-trends-${currentDealName}-${selectedPeriod}`}
                        data={currentFacility?.periods || {}}
                        type="EBITDAR"
                    />
                );
                break;
            case "ebitdar-margin-details":
                setTitle(`${groupPrefix}EBITDAR Margin Details`);
                setChartComponent(
                    <EbitdarTable
                        data={expenseData}
                        availableTimePeriods={availableTimePeriods}
                        currentPeriod={selectedPeriod}
                    />
                );
                break;
            case "ebitdar-margin-trends":
                setTitle(`${groupPrefix}EBITDAR Margin Trends`);
                setChartComponent(
                    <TrendChart
                        key={`ebitdar-margin-trends-${currentDealName}-${selectedPeriod}`}
                        data={currentFacility?.periods || {}}
                        type="EBITDAR"
                    />
                );
                break;
            default:
                setTitle("Finance Chart");
                setChartComponent(<div>Unknown chart type</div>);
        }

        // Mark initial load complete
        setIsInitialLoad(false);
    }, [chartType, currentFacility, currentDeal, currentDealName, isLoading, selectedPeriod, availableTimePeriods]);

    // Show loading state if data isn't ready
    if (isLoading || !currentFacility || !selectedPeriod) {
        return (
            <Card extra={"w-full p-6"}>
                <div className="w-full flex justify-center items-center p-8">
                    <div className="text-gray-500">
                        {isLoading ? `Loading financial data for selected ${viewMode === 'portfolio' ? 'portfolio' : 'deal'}...` : "Loading financial data..."}
                    </div>
                </div>
            </Card>
        );
    }

    // Show empty state if we couldn't get chart data
    if (!isInitialLoad && !chartComponent) {
        return (
            <Card extra={"w-full p-6"}>
                <h2 className="text-2xl font-bold mb-6 text-navy-700 dark:text-white">
                    {title || "Finance Chart"}
                </h2>
                <div className="w-full flex justify-center items-center p-8">
                    <div className="text-gray-500">No data available for this chart type.</div>
                </div>
            </Card>
        );
    }

    return (
        <Card extra={"w-full p-6"} key={`finance-chart-${renderKey}`}>
            <h2 className="text-2xl font-bold mb-6 text-navy-700 dark:text-white">
                {title}
            </h2>
            <div className="w-full">
                {chartComponent}
            </div>
        </Card>
    );
};

export default FinanceChartPage; 